import { FloorPlanTile } from "@faro-lotv/lotv";
import {
  Camera,
  Group,
  Matrix4,
  PlaneGeometry,
  Texture,
  Vector2,
  Vector3,
} from "three";
import { DynamicMapSource } from "./dynamic-map-source";
import { ScanTile, WantedTile, tileCacheKey } from "./dynamic-map-tile-store";
import {
  Rect2,
  TileKey,
  countVisibleTiles,
  parentOf,
  selectLevel,
  tileSizeMeters,
  tileWorldRect,
  visibleTiles,
} from "./tile-math";

/** Maximum number of tiles to request for a single view */
const MAX_TILES_PER_VIEW = 64;

const NDC_CORNERS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
];

type DrawnTile = { tile: ScanTile; texture: Texture };

type ViewFootprint = {
  /** Visible rectangle of the map plane, in map coordinates */
  rect: Rect2;
  /** Center of the view on the map plane */
  center: Vector2;
  /** Size of a device pixel on the map plane at the view center, undefined if the center does not hit the plane */
  metersPerPixel?: number;
};

/**
 * Renders the visible tiles of a dynamic overview map for one view (camera).
 *
 * The object must be placed with the matrix mapping the map frame (Z-up, meters) to the world,
 * the tiles are rendered on the plane z = elevation of the map frame.
 */
export class DynamicMapLayer extends Group {
  override name = "DynamicMapLayer";

  /** Integer added to the level of detail, positive values trade sharpness for fewer requests */
  levelBias = 0;

  #id = Symbol("DynamicMapLayer");
  #geometry = new PlaneGeometry();
  #tiles = new Map<string, FloorPlanTile>();
  #scans?: string;
  /** Scans of the last view whose tiles were all loaded, shown until the ones of the current scans are */
  #shownScans?: string;
  #elevation = 0;
  #wantedSignature = "";

  /** @param source The loaded map to render */
  constructor(private source: DynamicMapSource) {
    super();
    this.matrixAutoUpdate = false;
  }

  /**
   * Place the map in the world
   *
   * @param matrix mapping the map frame to the world
   * @param elevation of the map plane, in the map frame
   */
  setPlacement(matrix: Matrix4, elevation: number): void {
    this.matrix.copy(matrix);
    this.matrixWorldNeedsUpdate = true;
    this.#elevation = elevation;
  }

  /** @param scans compact list of scans to show, undefined to show none */
  setScans(scans: string | undefined): void {
    this.#scans = scans;
    if (!scans) this.#shownScans = undefined;
  }

  /**
   * Update the rendered tiles and request the missing ones for a view
   *
   * @param camera of the view
   * @param viewport size of the view in device pixels
   */
  update(camera: Camera, viewport: Vector2): void {
    const drawn = new Map<string, DrawnTile>();
    const wanted: WantedTile[] = [];
    const scans = this.#scans;

    if (scans && !this.source.tiles.stopped) {
      this.updateWorldMatrix(true, false);
      const footprint = this.#computeFootprint(camera, viewport);
      const toLoad = footprint ? this.#selectTiles(footprint) : [];
      for (const { key, priority } of toLoad) {
        wanted.push({ ...key, scans, priority });
      }

      // Swap all the tiles at once when the new scans are complete, instead of one by one as they arrive
      if (
        toLoad.every(({ key }) =>
          this.source.tiles.isSettled({ ...key, scans }),
        )
      ) {
        this.#shownScans = scans;
      }
      const scansList = [...new Set([this.#shownScans ?? scans, scans])];
      for (const { key } of toLoad) {
        if (
          !scansList.some((s) => this.#addIfLoaded({ ...key, scans: s }, drawn))
        ) {
          this.#addFallbacks(key, drawn, scansList);
        }
      }

      for (const { tile } of drawn.values()) {
        wanted.push({ ...tile, priority: 0 });
      }
    }

    this.#setWanted(wanted);
    this.#updateMeshes(drawn);
  }

  /** Release the tiles requested by this layer */
  dispose(): void {
    this.source.tiles.release(this.#id);
    this.#geometry.dispose();
  }

  #setWanted(wanted: WantedTile[]): void {
    const signature = wanted.map(tileCacheKey).join(";");
    if (signature === this.#wantedSignature) return;
    this.#wantedSignature = signature;
    this.source.tiles.setWanted(this.#id, wanted);
  }

  /**
   * @returns the part of the map plane covered by the camera frustum, undefined if it does not contain data
   * @param camera of the view
   * @param viewport size of the view in device pixels
   */
  #computeFootprint(
    camera: Camera,
    viewport: Vector2,
  ): ViewFootprint | undefined {
    const toMap = this.matrixWorld.clone().invert();
    const hit = (x: number, y: number): Vector3 | undefined =>
      hitPlane(camera, toMap, this.#elevation, x, y);

    // Corners looking above the horizon are replaced by the far end of the frustum
    const points = NDC_CORNERS.map(
      ([x, y]) => hit(x, y) ?? unprojectToMap(camera, toMap, x, y, 1),
    );

    const rect = clampRect(
      {
        minX: Math.min(...points.map((p) => p.x)),
        minY: Math.min(...points.map((p) => p.y)),
        maxX: Math.max(...points.map((p) => p.x)),
        maxY: Math.max(...points.map((p) => p.y)),
      },
      this.#dataBounds(),
    );
    if (!rect) return;

    const center = hit(0, 0);
    const next = hit(2 / viewport.x, 0);
    return {
      rect,
      center: center
        ? new Vector2(center.x, center.y)
        : new Vector2((rect.minX + rect.maxX) / 2, (rect.minY + rect.maxY) / 2),
      metersPerPixel: center && next ? center.distanceTo(next) : undefined,
    };
  }

  /** @returns the area where the map may contain data, undefined if unknown */
  #dataBounds(): Rect2 | undefined {
    const bounds = this.source.scanBounds;
    if (!bounds) return;
    const { metadata } = this.source;
    // A scan can not see further than the size of the coarsest tile
    const margin = tileSizeMeters(metadata, metadata.levels - 1);
    return {
      minX: bounds.minX - margin,
      minY: bounds.minY - margin,
      maxX: bounds.maxX + margin,
      maxY: bounds.maxY + margin,
    };
  }

  /** @returns the tiles to load for a view, sorted by priority */
  #selectTiles({
    rect,
    center,
    metersPerPixel,
  }: ViewFootprint): Array<{ key: TileKey; priority: number }> {
    const { metadata } = this.source;
    let level =
      metersPerPixel === undefined
        ? metadata.levels - 1
        : selectLevel(metadata, metersPerPixel, this.levelBias);
    while (
      level < metadata.levels - 1 &&
      countVisibleTiles(metadata, level, rect) > MAX_TILES_PER_VIEW
    ) {
      ++level;
    }

    const size = tileSizeMeters(metadata, level);
    let tilesRect = rect;
    if (countVisibleTiles(metadata, level, rect) > MAX_TILES_PER_VIEW) {
      // Even the coarsest level is too dense (e.g. perspective view towards the horizon): keep the tiles around the center
      const half = (Math.sqrt(MAX_TILES_PER_VIEW) / 2) * size;
      tilesRect = {
        minX: center.x - half,
        minY: center.y - half,
        maxX: center.x + half,
        maxY: center.y + half,
      };
    }

    return visibleTiles(metadata, level, tilesRect)
      .map((key) => ({
        key,
        priority: -center.distanceToSquared(
          new Vector2((key.x + 0.5) * size, (key.y + 0.5) * size),
        ),
      }))
      .sort((a, b) => b.priority - a.priority)
      .slice(0, MAX_TILES_PER_VIEW);
  }

  /**
   * Add the already loaded tiles that can replace a missing one
   *
   * @param key of the missing tile
   * @param drawn the tiles to draw, updated in place
   * @param scansList scan lists of the tiles that can be used, in order of preference
   */
  #addFallbacks(
    key: TileKey,
    drawn: Map<string, DrawnTile>,
    scansList: string[],
  ): void {
    if (key.level > 0) {
      for (const [dx, dy] of [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ]) {
        const child = {
          level: key.level - 1,
          x: 2 * key.x + dx,
          y: 2 * key.y + dy,
        };
        scansList.some((scans) =>
          this.#addIfLoaded({ ...child, scans }, drawn),
        );
      }
    }

    for (
      let ancestor = parentOf(key);
      ancestor.level < this.source.metadata.levels;
      ancestor = parentOf(ancestor)
    ) {
      const { level, x, y } = ancestor;
      if (
        scansList.some((scans) =>
          this.#addIfLoaded({ level, x, y, scans }, drawn),
        )
      ) {
        return;
      }
    }
  }

  /**
   * @returns true if the tile is loaded and was added to the tiles to draw
   * @param tile to add
   * @param drawn the tiles to draw, updated in place
   */
  #addIfLoaded(tile: ScanTile, drawn: Map<string, DrawnTile>): boolean {
    const texture = this.source.tiles.get(tile);
    if (texture) drawn.set(tileCacheKey(tile), { tile, texture });
    return !!texture;
  }

  /**
   * Sync the children meshes with the tiles to draw
   *
   * @param drawn the tiles to draw
   */
  #updateMeshes(drawn: Map<string, DrawnTile>): void {
    for (const [key, mesh] of this.#tiles) {
      if (!drawn.has(key)) {
        this.remove(mesh);
        this.#tiles.delete(key);
      }
    }

    const { metadata } = this.source;
    // Finer tiles are drawn first: the stencil test prevents coarser ones from covering them
    const sorted = [...drawn.entries()].sort(
      ([, a], [, b]) => a.tile.level - b.tile.level,
    );
    sorted.forEach(([key, { tile, texture }], index) => {
      let mesh = this.#tiles.get(key);
      if (!mesh) {
        mesh = new FloorPlanTile(
          texture,
          this.#geometry,
          this.source.material,
          metadata.levels - tile.level,
        );
        const rect = tileWorldRect(metadata, tile);
        mesh.position.set(
          (rect.minX + rect.maxX) / 2,
          (rect.minY + rect.maxY) / 2,
          0,
        );
        mesh.scale.set(rect.maxX - rect.minX, rect.maxY - rect.minY, 1);
        this.#tiles.set(key, mesh);
        this.add(mesh);
      }
      mesh.position.z = this.#elevation;
      // Negative orders draw the whole map before static sheets sharing the same group order
      mesh.renderOrder = index - sorted.length;
      mesh.onAfterRender =
        index === sorted.length - 1
          ? (renderer) => renderer.clearStencil()
          : () => {};
    });
  }
}

/**
 * @returns a point of the view frustum in map coordinates
 * @param camera of the view
 * @param toMap matrix from world to map coordinates
 * @param x NDC coordinate
 * @param y NDC coordinate
 * @param z NDC depth, -1 near plane, 1 far plane
 */
function unprojectToMap(
  camera: Camera,
  toMap: Matrix4,
  x: number,
  y: number,
  z: number,
): Vector3 {
  return new Vector3(x, y, z).unproject(camera).applyMatrix4(toMap);
}

/**
 * @returns where the view ray through a NDC point hits the map plane, undefined if it does not within the frustum
 * @param camera of the view
 * @param toMap matrix from world to map coordinates
 * @param elevation of the map plane
 * @param x NDC coordinate
 * @param y NDC coordinate
 */
function hitPlane(
  camera: Camera,
  toMap: Matrix4,
  elevation: number,
  x: number,
  y: number,
): Vector3 | undefined {
  const near = unprojectToMap(camera, toMap, x, y, -1);
  const far = unprojectToMap(camera, toMap, x, y, 1);
  const t = (elevation - near.z) / (far.z - near.z);
  return Number.isFinite(t) && t >= 0 && t <= 1 ? near.lerp(far, t) : undefined;
}

/**
 * @returns the intersection of two rectangles, undefined if empty
 * @param rect to clamp
 * @param bounds to clamp to, no clamping if undefined
 */
function clampRect(rect: Rect2, bounds: Rect2 | undefined): Rect2 | undefined {
  const clamped = bounds
    ? {
        minX: Math.max(rect.minX, bounds.minX),
        minY: Math.max(rect.minY, bounds.minY),
        maxX: Math.min(rect.maxX, bounds.maxX),
        maxY: Math.min(rect.maxY, bounds.maxY),
      }
    : rect;
  return clamped.minX < clamped.maxX && clamped.minY < clamped.maxY
    ? clamped
    : undefined;
}
